import type { ApiEnvelope } from '@/types/api.types';
import { apiClient } from '@lib/axios';

import type {
  ChartTemplate,
  ChartTemplateDetail,
  ChartTemplateListData,
  ChartTemplateListPagination,
  ListSystemChartTemplatesParams,
} from '../types';

/** List envelope carries pagination alongside the data array. */
interface ChartTemplateListEnvelope extends ApiEnvelope<ChartTemplate[]> {
  pagination: ChartTemplateListPagination;
}

const SYSTEM_BASE = '/catalog/system/chart-templates';

/**
 * List system-level chart templates. Public endpoint — no JWT required, though
 * the shared client will still attach a bearer token when one is available.
 */
export async function listSystemChartTemplates(
  params: ListSystemChartTemplatesParams = {},
): Promise<ChartTemplateListData> {
  const { data } = await apiClient.get<ChartTemplateListEnvelope>(SYSTEM_BASE, { params });
  return { items: data.data, pagination: data.pagination };
}

/** Fetch a single system template, including its active version definition. */
export async function getSystemChartTemplate(templateId: string): Promise<ChartTemplateDetail> {
  const { data } = await apiClient.get<ApiEnvelope<ChartTemplateDetail>>(
    `${SYSTEM_BASE}/${templateId}`,
  );
  return data.data;
}

/** Fetch the field definitions for a specific version of a system template. */
export async function getSystemChartTemplateVersion(
  templateId: string,
  version: number,
): Promise<ChartTemplateDetail> {
  const { data } = await apiClient.get<ApiEnvelope<ChartTemplateDetail>>(
    `${SYSTEM_BASE}/${templateId}/versions/${version}`,
  );
  return data.data;
}
